import { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { renderTurnstile, resetTurnstile } from '../utils/turnstile.js';
import { userSession } from '../utils/userSession.js';
import SocialAuthButtons from './SocialAuthButtons.js';

export default function CreateAccount({ isOpen, onClose, onLogin }) {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    partnerOptIn: false,
    turnstileToken: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    message: ''
  });

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        renderTurnstile((token) => {
          setFormData(prev => ({ ...prev, turnstileToken: token }));
        });
      }, 100);
    }
  }, [isOpen]);

  const clearForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      partnerOptIn: false,
      turnstileToken: ''
    });
    setPasswordStrength({
      score: 0,
      message: ''
    });
    setErrors({});
    resetTurnstile();
  };

  const validateEmail = (email) => {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return re.test(email);
  };

  const checkPasswordStrength = (password) => {
    let score = 0;
    let message = '';

    if (password.length >= 8) score++;
    if (password.match(/[a-z]/) && password.match(/[A-Z]/)) score++;
    if (password.match(/\d/)) score++;
    if (password.match(/[^a-zA-Z\d]/)) score++;

    switch (score) {
      case 0:
        message = 'Very weak';
        break;
      case 1:
        message = 'Weak';
        break;
      case 2:
        message = 'Fair';
        break;
      case 3:
        message = 'Good';
        break;
      case 4:
        message = 'Strong';
        break;
      default:
        message = '';
    }

    return { score, message };
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'password') {
      setPasswordStrength(checkPasswordStrength(value));
    }
  };

  const getStrengthColor = () => {
    switch (passwordStrength.score) {
      case 1: return 'bg-red-500';
      case 2: return 'bg-yellow-500';
      case 3: return 'bg-blue-500';
      case 4: return 'bg-green-500';
      default: return 'bg-gray-200';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccess(false);
    const newErrors = {};

    if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (passwordStrength.score < 3) {
      newErrors.password = 'Password is not strong enough';
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    if (!formData.turnstileToken) {
      newErrors.turnstile = 'Please complete the security check';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      setLoading(true);

      // Create account
      const response = await fetch('https://r7ej0e9i28.execute-api.ap-southeast-2.amazonaws.com/dev/createuser', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          password: formData.password,
          partnerOptIn: formData.partnerOptIn,
          turnstileToken: formData.turnstileToken
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        
        // Attempt to login automatically
        try {
          const loginResponse = await fetch('https://r7ej0e9i28.execute-api.ap-southeast-2.amazonaws.com/dev/login', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({ 
              email: formData.email,
              password: formData.password,
              turnstileToken: formData.turnstileToken
            }),
          });

          const loginData = await loginResponse.json();

          if (loginResponse.ok) {
            userSession.setUser(loginData);
            clearForm();
            setSuccess(false);
            onLogin(loginData);
            onClose();
          } else {
            throw new Error('Auto-login failed');
          }
        } catch (loginError) {
          console.error('Auto-login error:', loginError);
          setSuccess(true);
          clearForm();
        }
      } else {
        setErrors({ submit: data.message || 'Failed to create account' });
      }
    } catch (error) {
      console.error('Error details:', error);
      setErrors({ submit: 'An error occurred. Please try again.' });
      resetTurnstile();
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    clearForm();
    setSuccess(false);
    resetTurnstile();
    onClose();
  };

  return (
    <div 
      className={`fixed inset-y-0 right-0 w-96 bg-gray-50 shadow-lg transform transition-transform duration-300 ease-in-out ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      } z-50`}
    >
      <div className="relative h-full overflow-y-auto">
        <button
          onClick={handleClose}
          className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
        >
          <XMarkIcon className="h-6 w-6" />
        </button>

        <div className="flex flex-col px-6 pt-8">
          <div className="mx-auto w-full max-w-sm">
            <img
              alt="DriftCal"
              src="/driftcal_512_black.png"
              className="mx-auto h-10 w-auto"
            />
            <h2 className="mt-4 text-center text-xl font-bold text-gray-900">
              Create your account
            </h2>

            <div className="mt-6">
              <SocialAuthButtons
                active={isOpen}
                disabled={loading}
                onSuccess={(data) => {
                  userSession.setUser(data);
                  clearForm();
                  onLogin(data);
                  onClose();
                }}
              />
            </div>

            <form className="mt-2 space-y-4" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-900">
                  First Name
                </label>
                <div className="mt-1">
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    required
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className="block w-full rounded-md bg-white px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-slate-600"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-900">
                  Last Name
                </label>
                <div className="mt-1">
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    required
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className="block w-full rounded-md bg-white px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-slate-600"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-900">
                  Email address
                </label>
                <div className="mt-1">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    autoComplete="username"
                    className="block w-full rounded-md bg-white px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-slate-600"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-900">
                  Password
                </label>
                
                <div className="mt-1">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    required
                    value={formData.password}
                    onChange={handleInputChange}
                    autoComplete="new-password"
                    className="block w-full rounded-md bg-white px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-slate-600"
                  />
                  {formData.password && (
                    <div className="mt-1">
                      <div className="h-1 w-full bg-gray-200 rounded">
                        <div 
                          className={`h-1 rounded transition-all ${getStrengthColor()}`}
                          style={{ width: `${(passwordStrength.score / 4) * 100}%` }}
                        />
                      </div>
                      <p className="mt-1 text-sm text-gray-600">
                        Password strength: {passwordStrength.message}
                      </p>
                    </div>
                  )}
                  {errors.password && (
                    <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-900">
                  Confirm Password
                </label>
                <div className="mt-1">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    required
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    autoComplete="new-password"
                    className="block w-full rounded-md bg-white px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-slate-600"
                  />
                  {errors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
                  )}
                </div>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                  Password must contain:
                  <span className="block ml-2">• At least 8 characters</span>
                  <span className="block ml-2">• Upper and lowercase letters</span>
                  <span className="block ml-2">• At least one number</span>
                  <span className="block ml-2">• At least one special character</span>
                </p>
              <div className="flex items-center mt-4">
                <input
                  id="partnerOptIn"
                  name="partnerOptIn"
                  type="checkbox"
                  checked={formData.partnerOptIn}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    partnerOptIn: e.target.checked
                  }))}
                  className="h-4 w-4 rounded border-gray-300 text-slate-600 focus:ring-slate-600"
                />
                <label htmlFor="partnerOptIn" className="ml-2 block text-sm text-gray-900">
                  Receive Partner Offers? - Keep DriftCal alive!
                </label>
              </div>

              <div className="mt-4">
                <div id="turnstile-widget" className="flex justify-center"></div>
                {errors.turnstile && (
                  <p className="mt-1 text-sm text-red-600">{errors.turnstile}</p>
                )}
              </div>

              {success ? (
                <div className="text-sm text-green-600 text-center font-medium">
                  Account created successfully! You can now close and login.
                </div>
              ) : errors.submit && (
                <p className="text-sm text-red-600">{errors.submit}</p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-md bg-slate-900 px-3 py-1.5 text-sm font-semibold text-white hover:bg-slate-700 disabled:bg-slate-400 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
} 